import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { SegmentedControl } from '../components/SegmentedControl';
import { Switch } from '../components/Switch';
import { useToast } from '../components/Toast';
import { PlantProgress } from '../components/PlantProgress';
import { useNav } from '../state/NavContext';
import { clearAll } from '../state/persistence';
import { AVATARS, normalizeProfile, useProfile, type AvatarId } from '../state/ProfileContext';
import { FONT_SCALES, SKIN_LOCKED, useSettings, type ThemePref, type ThemeSkin } from '../state/SettingsContext';
import { looksLikeStats, overall } from '../state/statsLogic';
import { useToday } from '../hooks/useToday';
import { useStats } from '../state/StatsContext';
import { useSupporter } from '../state/SupporterContext';

const APP_VERSION = '0.1.0';

const SKINS: { id: ThemeSkin; name: string; color: string; locked: boolean }[] = [
  { id: 'default', name: 'Greenhouse', color: '#2f6b4f', locked: SKIN_LOCKED.default },
  { id: 'amber', name: 'Amber Grove', color: '#b58430', locked: SKIN_LOCKED.amber },
  { id: 'plum', name: 'Plum Orchard', color: '#7a4a86', locked: SKIN_LOCKED.plum },
];

/** Slices that survive "Reset everything": the paid unlock and the owner's site config. */
const KEEP_ON_RESET = ['supporter', 'site-config', 'admin-unlocked'];

/** A text field that edits locally and saves on blur / Enter (so spaces and clearing work). */
function NameField({ value, onSave }: { value: string; onSave: (name: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const name = draft.trim();
    if (name && name !== value) onSave(name);
    else setDraft(value);
  };
  return (
    <input
      className="cv-input"
      value={draft}
      maxLength={24}
      aria-label="Display name"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setDraft(value);
          e.currentTarget.blur();
        }
      }}
      style={{ minWidth: 0 }}
    />
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="cv-settings__group">
      <span className="cv-eyebrow">{title}</span>
      {children}
    </section>
  );
}

function Row({ title, desc, children }: { title: string; desc?: string; children: ReactNode }) {
  return (
    <div className="cv-setting-row">
      <div className="cv-setting-row__text">
        <span className="cv-setting-row__title">{title}</span>
        {desc ? <span className="cv-setting-row__desc">{desc}</span> : null}
      </div>
      {children}
    </div>
  );
}

export function SettingsScreen() {
  const today = useToday();
  const { settings, update, replaceAll: replaceSettings } = useSettings();
  const { profile, update: updateProfile, replaceAll: replaceProfile, reset: resetProfile } = useProfile();
  const { stats, replaceAll: replaceStats, reset: resetStats } = useStats();
  const { isSupporter, canSupport, canTip, openSupport, openTip, grant } = useSupporter();
  const { navigate } = useNav();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const exportData = () => {
    const payload = {
      app: 'cluevine',
      version: 2,
      exportedAt: Date.now(),
      settings,
      profile,
      stats,
      supporter: isSupporter || undefined,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cluevine-backup.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.show('Backup downloaded', 'success');
  };

  const importData = async (file: File) => {
    let data: unknown;
    try {
      data = JSON.parse(await file.text());
    } catch {
      toast.show('Could not read that file', 'error');
      return;
    }
    // Validate everything before applying anything: a wrong or damaged file must
    // not wipe the profile or leave stats that crash the app.
    const d = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const nextProfile = d ? normalizeProfile(d.profile) : null;
    if (!d || d.app !== 'cluevine' || !looksLikeStats(d.stats) || !nextProfile) {
      toast.show('That isn’t a ClueVine backup', 'error');
      return;
    }
    if (d.settings && typeof d.settings === 'object') replaceSettings(d.settings);
    replaceProfile(nextProfile);
    replaceStats(d.stats);
    if (d.supporter === true) grant();
    toast.show('Backup restored', 'success');
  };

  const doReset = () => {
    clearAll(KEEP_ON_RESET);
    resetStats();
    resetProfile();
    replaceSettings({ ...settings, theme: 'system' });
    setConfirmReset(false);
    toast.show('All data cleared');
  };

  return (
    <div className="cv-settings">
      <div className="cv-page-head">
        <span className="cv-eyebrow">Preferences</span>
        <h1>Settings</h1>
      </div>

      {profile ? (
        <Group title="Profile">
          <div className="cv-setting-row">
            <div className="cv-row" style={{ gap: 'var(--space-sm)' }}>
              <Avatar id={profile.avatar} size={44} />
              <NameField value={profile.name} onSave={(name) => updateProfile({ name })} />
            </div>
          </div>
          <div className="cv-avatar-grid">
            {AVATARS.map((a: AvatarId) => (
              <button
                key={a}
                type="button"
                className="cv-avatar-pick"
                aria-pressed={a === profile.avatar}
                aria-label={`${a} avatar`}
                onClick={() => updateProfile({ avatar: a })}
              >
                <Avatar id={a} size={40} />
              </button>
            ))}
          </div>
          <p className="cv-help">Your avatar is also your growing plant. Each one grows differently as your daily streak builds.</p>
          <PlantProgress streak={overall(stats, today).currentBestStreak} compact customize={false} />
        </Group>
      ) : null}

      <Group title="Appearance">
        <Row title="Theme">
          <SegmentedControl<ThemePref>
            ariaLabel="Theme"
            value={settings.theme}
            onChange={(t) => update({ theme: t })}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'system', label: 'Auto' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
        </Row>
        <Row title="Text size">
          <SegmentedControl<number>
            ariaLabel="Text size"
            value={settings.fontScale}
            onChange={(v) => update({ fontScale: v })}
            options={FONT_SCALES.map((value, i) => ({ value, label: ['S', 'M', 'L', 'XL'][i] }))}
          />
        </Row>
        <Row title="High contrast" desc="Firmer borders and stronger text">
          <Switch label="High contrast" checked={settings.highContrast} onChange={(v) => update({ highContrast: v })} />
        </Row>
        <div className="cv-skinrow">
          <div className="cv-setting-row__text">
            <span className="cv-setting-row__title">Theme skin</span>
            <span className="cv-setting-row__desc">
              {isSupporter ? 'Pick your palette' : 'Bonus palettes unlock with Supporter'}
            </span>
          </div>
          <div className="cv-skins">
            {SKINS.map((s) => {
              const locked = s.locked && !isSupporter;
              return (
                <button
                  key={s.id}
                  type="button"
                  className="cv-skin"
                  data-active={settings.skin === s.id ? 'true' : undefined}
                  aria-pressed={settings.skin === s.id}
                  aria-label={s.name}
                  onClick={() => (locked ? openSupport() : update({ skin: s.id }))}
                >
                  <span className="cv-skin__swatch" style={{ background: s.color }} />
                  <span>{s.name}</span>
                  {locked ? <span className="cv-skin__lock">🔒</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      </Group>

      <Group title="Clues & motion">
        <Row title="Color-blind clues" desc="Blue / orange clues with patterns">
          <Switch label="Color-blind clues" checked={settings.colorBlind} onChange={(v) => update({ colorBlind: v })} />
        </Row>
        <Row title="Reduce motion" desc="Minimize animations">
          <Switch label="Reduce motion" checked={settings.reducedMotion} onChange={(v) => update({ reducedMotion: v })} />
        </Row>
      </Group>

      <Group title="Sound & feel">
        <Row title="Sound effects">
          <Switch label="Sound effects" checked={settings.sound} onChange={(v) => update({ sound: v })} />
        </Row>
        <Row title="Haptics" desc="Vibration on supported devices">
          <Switch label="Haptics" checked={settings.haptics} onChange={(v) => update({ haptics: v })} />
        </Row>
      </Group>

      {isSupporter || canSupport || canTip ? (
        <Group title="Support">
          {isSupporter || canSupport ? (
            <Row
              title={isSupporter ? 'You’re a supporter 🌱' : 'Support ClueVine'}
              desc={
                isSupporter
                  ? 'Thank you! The ad is gone and your bonus theme skins are unlocked above.'
                  : 'A small one-time tip removes the ad and unlocks two bonus theme skins.'
              }
            >
              {isSupporter ? (
                <span className="cv-chip cv-chip--seed">Supporter</span>
              ) : (
                <Button variant="primary" size="sm" onClick={openSupport}>
                  Support
                </Button>
              )}
            </Row>
          ) : null}
          {canTip ? (
            <Row title="Tip jar" desc="Just want to say thanks? Leave a tip, no strings.">
              <Button variant="secondary" size="sm" onClick={openTip}>
                ☕ Tip jar
              </Button>
            </Row>
          ) : null}
        </Group>
      ) : null}

      <Group title="Advanced">
        <Row title="Hint nudges" desc="Gently offer a hint when you’re stuck">
          <Switch label="Hint nudges" checked={settings.hintNudges} onChange={(v) => update({ hintNudges: v })} />
        </Row>
        <Row title="FPS overlay" desc="Show a live performance readout">
          <Switch label="FPS overlay" checked={settings.perfOverlay} onChange={(v) => update({ perfOverlay: v })} />
        </Row>
      </Group>

      <Group title="Your data">
        <div className="cv-row cv-wrap">
          <Button variant="secondary" iconLeft="share" onClick={exportData}>
            Export backup
          </Button>
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>
            Import backup
          </Button>
          <Button variant="danger" onClick={() => setConfirmReset(true)}>
            Reset everything
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void importData(file);
              e.target.value = '';
            }}
          />
        </div>
        <p className="cv-help">
          ClueVine keeps everything on this device — no account, no servers. Export to move your garden
          elsewhere.
        </p>
      </Group>

      <div className="cv-center cv-stack" style={{ gap: 'var(--space-2xs)', alignItems: 'center' }}>
        <button type="button" className="cv-result__copylink" onClick={() => navigate('admin')}>
          Site admin
        </button>
        <p className="cv-faint" style={{ fontSize: 'var(--text-xs)' }}>
          ClueVine v{APP_VERSION}
        </p>
      </div>

      <Modal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset everything?"
        footer={
          <>
            <Button variant="ghost" block onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button variant="danger" block onClick={doReset}>
              Reset
            </Button>
          </>
        }
      >
        <p className="cv-muted">
          This clears your profile, stats, history, streaks, and saved puzzles on this device. Your
          Supporter unlock is kept. This can’t be undone — export a backup first if you might want it back.
        </p>
      </Modal>
    </div>
  );
}
